import { useQuery } from "@tanstack/react-query"

import { getSessionApi } from "@/api/api"
import { usePrismMemory } from "@/hooks/use-prism-memory"
import { SettingsService } from "@/services/settings-service"

/**
 * List-sessions never sets `memoryOffTransferred`. Only GET /api/get-session does.
 * A list row that omits the flag is not an authoritative "off".
 */
type ListSessionFlag = {
  id?: string
  memoryOffTransferred?: boolean
} | null

function readSettingsToken(): string | undefined {
  try {
    return SettingsService.getInstance().token
  } catch {
    return undefined
  }
}

export function useMemoryOffTransferred(
  sessionId: string | null | undefined,
  listSession?: ListSessionFlag
): boolean {
  const prismMemory = usePrismMemory()
  const enabled = prismMemory && typeof sessionId === "string" && sessionId.length > 0

  const sessionQuery = useQuery({
    queryKey: ["memory-off-transferred", sessionId ?? ""],
    queryFn: () => {
      if (!sessionId) {
        throw new Error("session id is required")
      }
      return getSessionApi(sessionId, readSettingsToken())
    },
    enabled,
    retry: false,
    staleTime: 0,
    gcTime: 1000 * 60 * 5,
    refetchOnWindowFocus: true,
  })

  if (!enabled) {
    return false
  }
  // get-session wins over the list payload, including when the list omits the flag.
  if (sessionQuery.data?.memoryOffTransferred === true) {
    return true
  }
  if (sessionQuery.isSuccess) {
    return false
  }
  return listSession?.memoryOffTransferred === true
}
